'use client';

import type { ReactNode } from 'react';

export function FinanceStatus({ value }: { value: string }) {
  return <span className={`business-finance-status ${value.toLowerCase()}`}>{label(value)}</span>;
}

export function FinanceModal({
  title,
  subtitle,
  children,
  onClose,
  footer,
  wide = false,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="business-source-modal-overlay">
      <div className={`business-source-modal business-finance-modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="business-source-modal-header">
          <div><p className="eyebrow">Business Reports</p><h3>{title}</h3>{subtitle && <span>{subtitle}</span>}</div>
          <button className="admin-modal-close" onClick={onClose} aria-label={`Close ${title}`}>&times;</button>
        </div>
        <div className="business-finance-modal-body">{children}</div>
        {footer && <div className="business-source-modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

export function FinanceField({
  label: fieldLabel,
  help,
  full = false,
  children,
}: {
  label: string;
  help?: string;
  full?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`business-source-field${full ? ' full' : ''}`}>
      <span>{fieldLabel}</span>
      {children}
      {help && <small>{help}</small>}
    </label>
  );
}

export function formatMoney(value: string | number, currency = 'INR') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

export function formatNumber(value: string | number) {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
}

export function dateOnly(value: string | null | undefined) {
  return value ? value.slice(0, 10) : '—';
}

export function dateTime(value: string | null | undefined) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-IN');
}

export function cleanSpaces(value: string) {
  return value.normalize('NFKC').trim().replace(/\s+/g, ' ');
}

export function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}

export function exportCsv(fileName: string, headers: string[], rows: Array<Array<unknown>>) {
  const quote = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map((row) => row.map(quote).join(',')).join('\r\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export function label(value: string) {
  return value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
